import Big from 'big.js';
import { getSecondaryRecipientContacts } from 'pages/contacts/contactRelationUtils';
import { findEmploymentOnDate } from 'pages/pricelists/matterRateResolution';
import { timecardSecondsToHours, toValueFactorOptions } from 'pages/timecards/utils/timecardUtils';
import { ClassifierData } from 'types/classifiers';
import { ContactDto, ContactMinDto } from 'types/contacts';
import { ExpenseDto } from 'types/expenses';
import {
    InvoiceExpense,
    InvoiceExpenseForm,
    InvoiceMatterAdjustmentDto,
    InvoiceMatterForm,
    InvoiceMatterSummary,
    InvoiceTimecard,
    InvoiceTimecardForm,
    InvoiceTimecardUpdateDto,
} from 'types/invoice-details';
import {
    EditIntentTypeCode,
    ExistingPrepaymentForm,
    GroupingInvoiceRowEditForm,
    GroupingInvoiceRowUpdateDto,
    InvoiceData,
    InvoiceDetailsTotals,
    InvoiceDetailsTotalsFormDto,
    InvoiceExpenseTotals,
    InvoiceForm,
    InvoiceMatterDetailsDto,
    InvoicePartyRoleTypeCode,
    InvoiceRowCreateDto,
    InvoiceRowForm,
    InvoiceRowTotals,
    InvoiceRowTotalsFormDto,
    InvoiceRowUpdateDto,
    InvoiceTimecardTotals,
    InvoiceTotals,
    InvoiceTotalsForm,
    InvoiceTotalsFormDto,
    InvoiceUpdateDto,
    PrepaymentDataListItem,
    UpdateInvoiceLimitedDto,
} from 'types/invoices';
import { RemunerationCalcType } from 'types/matters';
import { getOptionsFromClassifier } from 'utils/formUtil';
import { sortByField } from 'utils/sort';

/**
 * `useForm({ resetOptions })` is merged into every manual `reset()` call by react-hook-form, so the
 * `keepDirtyValues` we need for the `values` sync also leaks into the post-save reset and would keep
 * `dirtyFields` populated. A field left permanently dirty is then never re-synced from the server
 * payload, which keeps the form dirty forever when the picked option is a leaner DTO than the one
 * the invoice returns (payer, receiver). Turning it off here restores a clean slate after a save.
 */
export const INVOICE_FORM_POST_SAVE_RESET_OPTIONS = {
    keepValues: true,
    keepDirty: false,
    keepDirtyValues: false,
    keepDefaultValues: false,
} as const;

export const mapInvoiceDataToInvoiceFormDto = (
    invoice: InvoiceData,
    classifiers: Record<string, ClassifierData>
): InvoiceForm => {
    return {
        isPaymentCurrencyDifferent: invoice.paymentCurrencyTypeCode !== invoice.invoiceCurrencyTypeCode,
        paymentCurrencyType:
            (
                getOptionsFromClassifier(classifiers['CURRENCY']) as {
                    label: string;
                    value: string;
                }[]
            ).find((val) => val.value === invoice.paymentCurrencyTypeCode) ?? null,
        clientParty: invoice.clientParty,
        receiverParty: invoice.receiverParty,
        dueDate: invoice.dueDate ?? '',
        issueDate: invoice.issueDate ?? '',
        serviceDate: invoice.serviceDate ?? '',
        maxInvoiceTimecardOrExpenseDate: invoice.maxInvoiceTimecardOrExpenseDate ?? '',
        invoiceCurrencyType:
            (
                getOptionsFromClassifier(classifiers['CURRENCY']) as {
                    label: string;
                    value: string;
                }[]
            ).find((val) => val.value === invoice.invoiceCurrencyTypeCode) ?? null,
        invoiceAccountingType:
            (
                getOptionsFromClassifier(classifiers['INVOICE_ACCOUNTING_TYPE']) as {
                    label: string;
                    value: string;
                }[]
            ).find((val) => val.value === invoice.invoiceAccountingTypeCode) ?? null,
        vatCalculationType:
            (
                getOptionsFromClassifier(classifiers['VAT_CALCULATION_TYPE']) as {
                    label: string;
                    value: string;
                }[]
            ).find((val) => val.value === invoice.vatCalculationTypeCode) ?? null,
        invoiceDate: invoice.invoiceDate,
        paymentCurrencyConversionDate: invoice.paymentCurrencyConversionDate ?? '',
        baseCurrencyConversionDate: invoice.baseCurrencyConversionDate ?? '',
        invoiceDescription: invoice.invoiceDescription ?? '',
        invoiceDescriptionLocalLanguage: invoice.invoiceDescriptionLocalLanguage ?? '',
        outlineDescription: invoice.outlineDescription ?? '',
        outlineDescriptionLocalLanguage: invoice.outlineDescriptionLocalLanguage ?? '',
        invoiceNumber: invoice.invoiceNumber,
        invoiceTemplate: invoice.invoiceTemplate
            ? { id: invoice.invoiceTemplate.id, name: invoice.invoiceTemplate.name }
            : null,
        invoiceSpecTemplate: invoice.invoiceSpecTemplate
            ? { id: invoice.invoiceSpecTemplate.id, name: invoice.invoiceSpecTemplate.name }
            : null,
        invoicedByOffice: invoice.invoicedByOffice,
        primaryRecipientAdditionalEmails: invoice.primaryRecipientAdditionalEmails ?? '',
        invoiceTransitions: [],
        primaryRecipientParty: invoice.primaryRecipientParty?.id ? invoice.primaryRecipientParty : null,
        secondaryRecipientAdditionalEmails: invoice.secondaryRecipientAdditionalEmails ?? '',
        secondaryRecipientParties: sortByField(
            (invoice.parties ?? []).flatMap(({ invoicePartyRoleTypeCode, party }) =>
                invoicePartyRoleTypeCode === InvoicePartyRoleTypeCode.SECONDARY_RECIPIENT ? [party] : []
            ),
            'name'
        ),
        paymentNotificationReceivers: invoice.paymentNotificationReceivers?.map((item) => item.employee) ?? [],
        signedByEmployee: invoice.signedByEmployee,
        supervisedByEmployee: invoice.supervisedByEmployee,
        billingInstructions: invoice.billingInstructions ?? '',
        additionalAddress: invoice.additionalAddress ?? '',
        additionalInfo: invoice.additionalInfo ?? '',
        excludeFromReporting: invoice.excludeFromReporting ?? false,
        isSpecIncludedInInvoice: invoice.isSpecIncludedInInvoice ?? false,
        isSpecExcludedFromEmail: invoice.isSpecExcludedFromEmail ?? false,
        nextDebtCollectionActionType:
            (
                getOptionsFromClassifier(classifiers['NEXT_DEBT_COLLECTION_ACTION_TYPE']) as {
                    label: string;
                    value: string;
                }[]
            ).find((val) => val.value === invoice.nextDebtCollectionActionTypeCode) ?? null,
        receivabilityType:
            (
                getOptionsFromClassifier(classifiers['RECEIVABILITY_TYPE']) as {
                    label: string;
                    value: string;
                }[]
            ).find((val) => val.value === invoice.receivabilityTypeCode) ?? null,
        penaltyAmount: invoice.penaltyAmount?.toString() ?? null,
        interestPaidOn: invoice.interestPaidOn ?? null,
        nextReminderDate: invoice.nextReminderDate ?? null,
        serviceStartDate: invoice.serviceStartDate ?? '',
        serviceEndDate: invoice.serviceEndDate ?? '',
        debtNotes: invoice.debtNotes ?? null,
        interestRate: invoice.interestRate?.toString() ?? null,
        downloadButtonsDisabled: invoice.downloadButtonsDisabled ?? false,
        isDisplayedInPaymentCurrency: invoice.isDisplayedInPaymentCurrency ?? false,
        invoiceDateManuallyUpdated: false,
        issueDateManuallyUpdated: false,
        dueDateManuallyUpdated: false,
    };
};

export const mapInvoiceTotalsToInvoiceTotalsFormDto = (invoiceTotals?: InvoiceTotals): InvoiceTotalsForm => {
    return {
        invoiceDetailsTotals:
            (invoiceTotals?.invoiceDetailsTotals ?? []).map(mapInvoiceDetailsTotalsToInvoiceTotalsFormDto) ?? [],
    };
};

const mapInvoiceDetailsTotalsToInvoiceTotalsFormDto = (
    invoiceDetailsTotals: InvoiceDetailsTotals
): InvoiceDetailsTotalsFormDto => {
    const allRows = invoiceDetailsTotals?.invoiceRowTotals ?? [];
    const adminFeeRows = allRows.filter((row) => row.invoiceRowTypeCode === 'ADMIN_FEE');
    const otherRows = allRows.filter((row) => row.invoiceRowTypeCode !== 'ADMIN_FEE');

    return {
        matterId: invoiceDetailsTotals.matterId ?? 0,
        matterNumber: invoiceDetailsTotals.matterNumber ?? '',
        matterName: invoiceDetailsTotals.matterNumber ?? '',
        fullName: invoiceDetailsTotals.fullName ?? '',
        invoiceTimecardTotals:
            (invoiceDetailsTotals?.invoiceTimecardTotals ?? []).map(mapInvoiceTimecardTotalsToInvoiceTotalsFormDto) ??
            [],
        invoiceExpenseTotals:
            (invoiceDetailsTotals?.invoiceExpenseTotals ?? []).map(mapInvoiceExpenseTotalsToInvoiceTotalsFormDto) ?? [],
        invoiceRowTotals: otherRows.map(mapInvoiceRowTotalsToInvoiceTotalsFormDto),
        adminFeeRowTotals: adminFeeRows.map(mapInvoiceRowTotalsToInvoiceTotalsFormDto),
    };
};

const mapInvoiceTimecardTotalsToInvoiceTotalsFormDto = (
    invoiceTimecardTotals: InvoiceTimecardTotals
): InvoiceTotalsFormDto => {
    return {
        description: invoiceTimecardTotals.description ?? '',
        discountRate: invoiceTimecardTotals.discountRate ?? 0,
        totalWoVat: invoiceTimecardTotals.totalAfterAdjustments ?? 0,
        vatRate: invoiceTimecardTotals.vatRate ?? 0,
        totalWithVat: invoiceTimecardTotals.totalAfterAdjustmentsWithVat ?? 0,
    };
};

const mapInvoiceExpenseTotalsToInvoiceTotalsFormDto = (
    invoiceExpenseTotals: InvoiceExpenseTotals
): InvoiceTotalsFormDto => {
    return {
        description: invoiceExpenseTotals.description ?? '',
        discountRate: invoiceExpenseTotals.discountRate ?? 0,
        totalWoVat: invoiceExpenseTotals.totalAfterAdjustments ?? 0,
        vatRate: invoiceExpenseTotals.vatRate ?? 0,
        totalWithVat: invoiceExpenseTotals.totalAfterAdjustmentsWithVat ?? 0,
    };
};

const mapInvoiceRowTotalsToInvoiceTotalsFormDto = (invoiceRowTotals: InvoiceRowTotals): InvoiceRowTotalsFormDto => {
    return {
        description: invoiceRowTotals.description ?? '',
        discountRate: invoiceRowTotals.discountRate ?? 0,
        totalWoVat: invoiceRowTotals.totalWoVat ?? 0,
        vatRate: invoiceRowTotals.vatRate ?? 0,
        totalWithVat: invoiceRowTotals.totalWithVat ?? 0,
        id: invoiceRowTotals.invoiceRowId,
        editIntentTypeCode: invoiceRowTotals.editIntentTypeCode,
        selected: false,
    };
};

export const mapGroupingInvoiceRowEditFormToUpdateDto = (
    form: GroupingInvoiceRowEditForm,
    invoiceId: number
): GroupingInvoiceRowUpdateDto => {
    return {
        invoiceId,
        description: form.description,
        totalAmount: form.totalAmount,
        totalBeforeAdjustment: form.amountBeforeDiscount,
        vatRate: form.vatRate,
        isDeleted: form.isDeleted,
        discountRate: form.discountRate,
        groupingRowId: form.groupingRowId,
        sourceType: form.sourceType,
        matterId: form.matterId,
    };
};

export const mapInvoiceRowFormToInvoiceRowUpdateDto = (
    invoiceRowEditForm: InvoiceRowForm,
    invoiceId: number
): InvoiceRowUpdateDto => {
    return {
        id: invoiceRowEditForm.invoiceRowId,
        invoiceId,
        description: invoiceRowEditForm.description ?? '',
        totalAmount: invoiceRowEditForm.itemTotal ?? 0,
        discountRate: invoiceRowEditForm.discountRate ?? 0,
        totalBeforeAdjustment: invoiceRowEditForm.amountBeforeDiscount,
    };
};

export const mapInvoiceRowFormToInvoiceRowCreateDto = (
    invoiceRowEditForm: InvoiceRowForm,
    invoiceId: number
): InvoiceRowCreateDto => {
    return {
        invoiceId,
        matterId: invoiceRowEditForm.matterId ?? 0,
        description: invoiceRowEditForm.description ?? '',
        totalAmount: invoiceRowEditForm.itemTotal ?? 0,
        discountRate: invoiceRowEditForm.discountRate ?? 0,
        totalBeforeAdjustment: invoiceRowEditForm.amountBeforeDiscount,
    };
};

export const mapInvoiceRowFormToAdminFeeCreateDto = (
    invoiceRowEditForm: InvoiceRowForm,
    invoiceId: number
): InvoiceRowCreateDto => {
    return {
        invoiceId,
        matterId: invoiceRowEditForm.matterId ?? 0,
        description: invoiceRowEditForm.description ?? '',
        totalAmount: invoiceRowEditForm.itemTotal ?? 0,
        discountRate: 0,
        invoiceRowTypeCode: 'ADMIN_FEE',
        adminFeeRate: invoiceRowEditForm.adminFeeRate,
    };
};

export const mapInvoiceRowFormToAdminFeeUpdateDto = (
    invoiceRowEditForm: InvoiceRowForm,
    invoiceId: number
): InvoiceRowUpdateDto => {
    return {
        id: invoiceRowEditForm.invoiceRowId,
        invoiceId,
        description: invoiceRowEditForm.description ?? '',
        totalAmount: invoiceRowEditForm.itemTotal ?? 0,
        discountRate: 0,
        adminFeeRate: invoiceRowEditForm.adminFeeRate,
    };
};

export const mapToExistingPrepaymentForm = (prepayments: PrepaymentDataListItem[]): ExistingPrepaymentForm[] => {
    return (prepayments ?? []).map((prepayment: PrepaymentDataListItem) => {
        return {
            id: prepayment.id,
            invoiceDate: prepayment.invoiceDate,
            invoiceDescription: prepayment.invoiceDescription,
            payments: prepayment.payments,
            totalAmount: prepayment.totalAmount,
            usedAmount: prepayment.usedAmount,
            remainingAmount: prepayment.remainingAmount,
            usedAmountByCurrentInvoice: prepayment.usedAmountByCurrentInvoice,
            isConnected: prepayment.isConnected,
            mainMatter: prepayment.mainMatter,
        };
    });
};

export const mapInvoiceFormToInvoiceUpdateDto = (form: InvoiceForm, id: number): InvoiceUpdateDto => {
    return {
        id: id,
        clientParty: { id: form.clientParty.id, name: form.clientParty.name },
        receiverParty: form.receiverParty?.id ? { id: form.receiverParty.id, name: form.receiverParty.name } : null,
        paymentCurrencyTypeCode: form.paymentCurrencyType?.value ?? null,
        dueDate: form?.dueDate ?? null,
        issueDate: form?.issueDate ?? null,
        serviceDate: form?.serviceDate ?? null,
        invoiceAccountingTypeCode: form?.invoiceAccountingType?.value ?? null,
        vatCalculationTypeCode: form?.vatCalculationType?.value ?? null,
        invoiceDate: form?.invoiceDate ?? null,
        paymentCurrencyConversionDate: form?.paymentCurrencyConversionDate ?? null,
        baseCurrencyConversionDate: form?.baseCurrencyConversionDate ?? null,
        invoiceDescription: form.invoiceDescription || null,
        invoiceDescriptionLocalLanguage: form.invoiceDescriptionLocalLanguage || null,
        outlineDescription: form.outlineDescription || null,
        outlineDescriptionLocalLanguage: form.outlineDescriptionLocalLanguage || null,
        invoiceNumber: form?.invoiceNumber ?? '',
        invoiceTemplate: form?.invoiceTemplate?.id ?? null,
        invoiceSpecTemplate: form?.invoiceSpecTemplate?.id ?? null,
        invoicedByOffice: form?.invoicedByOffice?.id
            ? { id: form.invoicedByOffice.id, name: form.invoicedByOffice.name }
            : null,
        primaryRecipientAdditionalEmails: form.primaryRecipientAdditionalEmails || null,
        primaryRecipientParty: form.primaryRecipientParty?.id ? { id: form.primaryRecipientParty.id } : null,
        secondaryRecipientAdditionalEmails: form.secondaryRecipientAdditionalEmails || null,
        secondaryRecipientParties: form.secondaryRecipientParties.map(({ id }) => ({ id })),
        signedByEmployee: form.signedByEmployee ?? null,
        billingInstructions: form.billingInstructions || null,
        additionalAddress: form?.additionalAddress || null,
        additionalInfo: form?.additionalInfo || null,
        excludeFromReporting: form?.excludeFromReporting ?? false,
        isSpecIncludedInInvoice: form?.isSpecIncludedInInvoice ?? false,
        isSpecExcludedFromEmail: form?.isSpecExcludedFromEmail ?? false,
        serviceStartDate: form?.serviceStartDate ?? null,
        serviceEndDate: form?.serviceEndDate ?? null,
        paymentNotificationReceivers: form.paymentNotificationReceivers,
        isDisplayedInPaymentCurrency: form.isDisplayedInPaymentCurrency,
        invoiceDateManuallyUpdated: form.invoiceDateManuallyUpdated,
        issueDateManuallyUpdated: form.issueDateManuallyUpdated,
        dueDateManuallyUpdated: form.dueDateManuallyUpdated,
    };
};

export const mapContactToInvoiceRecipientFields = (
    contact: ContactDto
): Pick<
    InvoiceForm,
    | 'primaryRecipientParty'
    | 'primaryRecipientAdditionalEmails'
    | 'secondaryRecipientParties'
    | 'secondaryRecipientAdditionalEmails'
> => ({
    primaryRecipientParty: contact.primaryRecipientParty,
    primaryRecipientAdditionalEmails: contact.primaryRecipientAdditionalEmails,
    secondaryRecipientParties: getSecondaryRecipientContacts(contact.relations) as ContactMinDto[],
    secondaryRecipientAdditionalEmails: contact.secondaryRecipientAdditionalEmails,
});

export const mapInvoiceFormToUpdateInvoiceLimitedDto = (form: InvoiceForm, id: number): UpdateInvoiceLimitedDto => {
    return {
        id: id,
        primaryRecipientAdditionalEmails: form.primaryRecipientAdditionalEmails || null,
        primaryRecipientParty: form.primaryRecipientParty?.id ? { id: form.primaryRecipientParty.id } : null,
        secondaryRecipientAdditionalEmails: form.secondaryRecipientAdditionalEmails || null,
        secondaryRecipientParties: form.secondaryRecipientParties.map(({ id }) => ({ id })),
        billingInstructions: form.billingInstructions || null,
        excludeFromReporting: form?.excludeFromReporting ?? false,
    };
};

export const mapInvoiceTimecardToInvoiceTimecardForm = (
    invoiceTimecard: InvoiceTimecard,
    classifiers: Record<string, ClassifierData>,
    isTwoDecimalEntryEnabled: boolean,
    isDisplayedInPaymentCurrency = false
): InvoiceTimecardForm => {
    return {
        id: invoiceTimecard.id,
        billedTimeInHours:
            invoiceTimecard.editIntentTypeCode === EditIntentTypeCode.MARK_AS_NONBILLABLE &&
            !invoiceTimecard.preserveNonBillableHours
                ? '0'
                : String(timecardSecondsToHours(invoiceTimecard.billedTimeInSeconds ?? 0, isTwoDecimalEntryEnabled)),
        billedTimeInSeconds: invoiceTimecard.billedTimeInSeconds ?? null,
        totalTimeInHours: String(
            timecardSecondsToHours(invoiceTimecard.totalTimeInSeconds ?? 0, isTwoDecimalEntryEnabled)
        ),
        totalTimeInSeconds: invoiceTimecard.totalTimeInSeconds ?? null,
        description: invoiceTimecard.description ?? '',
        discountAmount: invoiceTimecard.discountAmount ?? 0,
        adjustmentAmount: invoiceTimecard.adjustmentAmount ?? 0,
        discountTypeCode: invoiceTimecard.discountTypeCode ?? null,
        employee: invoiceTimecard.employee ?? null,
        hourlyPrice: isDisplayedInPaymentCurrency
            ? (invoiceTimecard?.hourlyPriceInPaymentCurrency ?? null)
            : (invoiceTimecard?.hourlyPrice ?? null),
        isHourlyPriceManuallyAdjusted: null,
        timecardDate: invoiceTimecard.timecardDate ?? null,
        totalAmount: invoiceTimecard.totalAmount ?? 0,
        multiplierType: getMultiplierTypeOptions(classifiers).find(
            (val) => val.value === invoiceTimecard.multiplierTypeCode
        ) ?? { value: 'NONE', label: '1.0x' },
        multiplierRate: invoiceTimecard.multiplierRate ?? 1.0,
        isMirrored: invoiceTimecard.isMirrored,
        isHidden: invoiceTimecard.isHidden,
        discountRate: invoiceTimecard.discountRate ?? 0,
        manualAdjustmentAmount: isDisplayedInPaymentCurrency
            ? (invoiceTimecard?.manualAdjustmentAmountInPaymentCurrency ?? null)
            : (invoiceTimecard?.manualAdjustmentAmount ?? null),
        matter: invoiceTimecard.matter ?? null,
        newMatter: invoiceTimecard.newMatter
            ? { id: invoiceTimecard.newMatter.id, name: invoiceTimecard.newMatter.fullName }
            : null,
        matterAssignment: invoiceTimecard.matterAssignment ?? null,
        newMatterAssignment: invoiceTimecard.newMatterAssignment ?? null,
        timecardActivityType:
            (
                getOptionsFromClassifier(classifiers['TIMECARD_ACTIVITY_TYPE']) as {
                    label: string;
                    value: string;
                }[]
            ).find((val) => val.value === invoiceTimecard.timecardActivityTypeCode) ?? null,
        editIntentTypeCode: invoiceTimecard.editIntentTypeCode ?? null,
        isNonBillable: invoiceTimecard.isNonBillable,
        preserveNonBillableHours: invoiceTimecard.preserveNonBillableHours,
        timecard: invoiceTimecard.timecard ?? null,
        selected: false,
        totalBeforeAdjustment: isDisplayedInPaymentCurrency
            ? (invoiceTimecard?.totalBeforeAdjustmentInPaymentCurrency ?? '')
            : (invoiceTimecard?.totalBeforeAdjustment ?? ''),
        jobPositionName:
            invoiceTimecard.timecardDate && invoiceTimecard.employee?.employmentHistory
                ? findEmploymentOnDate(
                      invoiceTimecard.employee.employmentHistory,
                      new Date(invoiceTimecard.timecardDate)
                  )?.jobPositionName || ''
                : invoiceTimecard.employee?.latestEmployment?.jobPositionName || '',
        isPositionTextInputEnabled: !!invoiceTimecard.positionText,
        positionText: invoiceTimecard.positionText || '',
        remunerationCalcType: invoiceTimecard.remunerationCalcTypeCode,
        remunerationTimeInSeconds: invoiceTimecard.remunerationTimeInSeconds ?? 0,
        remunerationTimeInHours: String(
            timecardSecondsToHours(invoiceTimecard.remunerationTimeInSeconds ?? 0, isTwoDecimalEntryEnabled)
        ),
        remEqualsUsedTime: invoiceTimecard.remunerationCalcTypeCode === RemunerationCalcType.EQUALS_USED_TIME,
        isTimecardForInputEnabled: !!invoiceTimecard.timecardForLabel,
        timecardForLabel: invoiceTimecard.timecardForLabel,
        billingUpliftValueFactors: toValueFactorOptions(invoiceTimecard.billingUpliftValueFactors, classifiers),
        valueFactorsNotes: invoiceTimecard.valueFactorsNotes ?? '',
        notes: invoiceTimecard.notes ?? '',
    };
};

/**
 * Only a price the user typed is sent. Without one the backend keeps the stored price, priced or not, and
 * re-prices the line from the case's lists itself when its date, person or case changed.
 */
export function mapInvoiceTimecardFormToUpdateDto(
    formData: InvoiceTimecardForm,
    invoiceId: number
): InvoiceTimecardUpdateDto {
    return {
        ...formData,
        invoiceId,
        timecardActivityTypeCode: formData.timecardActivityType?.value ?? null,
        remunerationCalcTypeCode: formData.remunerationCalcType,
        multiplierTypeCode: formData.multiplierType?.value ?? null,
        timecardForLabel: formData.isTimecardForInputEnabled ? formData.timecardForLabel || null : null,
        billingUpliftValueFactors: formData.billingUpliftValueFactors.map((option) => option.value),
        valueFactorsNotes: formData.valueFactorsNotes || null,
        notes: formData.notes ?? null,
        hourlyPrice: formData.isHourlyPriceManuallyAdjusted === true ? formData.hourlyPrice : null,
    };
}

export const mapInvoiceExpenseToInvoiceExpenseForm = (
    invoiceExpense: InvoiceExpense,
    classifiers: Record<string, ClassifierData>,
    isDisplayedInPaymentCurrency = false
): InvoiceExpenseForm => {
    const currencyClassifiers = getOptionsFromClassifier(classifiers['CURRENCY']) as {
        label: string;
        value: string;
    }[];
    const displayAmount = isDisplayedInPaymentCurrency
        ? (invoiceExpense.expenseAmountInPaymentCurrency ?? 0)
        : (invoiceExpense.expenseAmount ?? 0);
    return {
        id: invoiceExpense.id,
        employee: invoiceExpense.employee ?? null,
        expenseDate: invoiceExpense.expenseDate ?? null,
        description: invoiceExpense.description ?? '',
        isNonBillable: invoiceExpense.isNonBillable ?? null,
        isMirrored: invoiceExpense.isMirrored,
        isHidden: invoiceExpense.isHidden,
        expenseType:
            (
                getOptionsFromClassifier(classifiers['EXPENSE_TYPE']) as {
                    label: string;
                    value: string;
                }[]
            ).find((val) => val.value === invoiceExpense.expenseTypeCode) ?? null,
        expenseAmount: displayAmount,
        currencyType: currencyClassifiers.find((val) => val.value === invoiceExpense.currencyTypeCode) ?? null,
        discountAmount: invoiceExpense.discountAmount ?? 0,
        discountTypeCode: invoiceExpense.discountTypeCode ?? null,
        matter: invoiceExpense.matter ?? null,
        newMatter: invoiceExpense.newMatter
            ? { id: invoiceExpense.newMatter.id, name: invoiceExpense.newMatter.fullName }
            : null,
        editIntentTypeCode: invoiceExpense.editIntentTypeCode ?? null,
        expense: invoiceExpense.expense ? mapExpenseToExpenseForm(invoiceExpense.expense, classifiers) : null,
        selected: false,
    };
};

const mapExpenseToExpenseForm = (expense: ExpenseDto, classifiers: Record<string, ClassifierData>): ExpenseDto => {
    return {
        id: expense.id,
        description: expense.description,
        isNonBillable: expense.isNonBillable,
        matter: expense.matter,
        timecardId: expense.timecardId,
        employeeId: expense.employeeId,
        employee: expense.employee,
        expenseTypeCode: expense.expenseTypeCode,
        expenseTypeName: (
            getOptionsFromClassifier(classifiers['EXPENSE_TYPE']) as {
                label: string;
                value: string;
            }[]
        ).find((val) => val.value === expense.expenseTypeCode)?.label,
        expenseAmount: expense.expenseAmount,
        currencyTypeCode: expense.currencyTypeCode,
        expenseDate: expense.expenseDate,
    };
};

export type MultiplierType = {
    value: string;
    label: string;
    multiplierRate: string;
};

export const getMultiplierTypeOptions = (classifiers: Record<string, ClassifierData>): MultiplierType[] => {
    const classifier = classifiers['MULTIPLIER_TYPE'];
    if (!classifier) {
        return [];
    }

    return classifier.values
        ? classifier.values
              .map((classifierValue) => ({
                  value: classifierValue.code,
                  label: `${
                      classifierValue.parameters
                          ? parseFloat(classifierValue.parameters['multiplierRate'] ?? '0').toFixed(1) + 'x'
                          : ''
                  }`,
                  multiplierRate: classifierValue.parameters?.['multiplierRate'] ?? '0',
              }))
              .sort((a, b) => parseFloat(a.multiplierRate) - parseFloat(b.multiplierRate))
        : [];
};

export const mapInvoiceMatterDetailsToInvoiceMatterForm = (
    classifiers: Record<string, ClassifierData>,
    invoiceMatter: InvoiceMatterDetailsDto,
    summaryData: InvoiceMatterSummary,
    isTwoDecimalEntryEnabled: boolean,
    isDisplayedInPaymentCurrency = false
): InvoiceMatterForm => ({
    matterId: invoiceMatter.matterId,
    discountRate: summaryData.discountRate ?? null,
    discountAmount: (() => {
        const log = isDisplayedInPaymentCurrency
            ? summaryData.calculationLog?.paymentCurrency
            : summaryData.calculationLog;
        return log?.total?.matterDiscountAmount
            ? new Big(Math.round(log.total.matterDiscountAmount * 100) / 100).toFixed(2)
            : '';
    })(),
    adjustmentAmount: summaryData.adjustmentAmount ?? null,
    invoiceExpenses: (summaryData.invoiceExpenses ?? []).map((val) =>
        mapInvoiceExpenseToInvoiceExpenseForm(val, classifiers, isDisplayedInPaymentCurrency)
    ),
    invoiceTimecards: (summaryData.invoiceTimecards ?? []).map((invoiceTimecard) =>
        mapInvoiceTimecardToInvoiceTimecardForm(
            invoiceTimecard,
            classifiers,
            isTwoDecimalEntryEnabled,
            isDisplayedInPaymentCurrency
        )
    ),
    invoiceAssignments: summaryData.invoiceAssignments ?? [],
    invoiceMatterName: invoiceMatter.invoiceMatterName ?? '',
    invoiceMatterNameLocalLanguage: invoiceMatter.invoiceMatterNameLocalLanguage ?? '',
    specificationInvoiceMatterName: invoiceMatter.specificationInvoiceMatterName ?? '',
    specificationInvoiceMatterNameLocalLanguage: invoiceMatter.specificationInvoiceMatterNameLocalLanguage ?? '',
    adjustmentAmountForFees: summaryData.adjustmentAmountForFees ?? null,
    adjustmentAmountForExpensesWithoutVat: summaryData.adjustmentAmountForExpensesWithoutVat ?? null,
    adjustmentAmountForExpensesWithVat: summaryData.adjustmentAmountForExpensesWithVat ?? null,
    capRemainingBeforeInvoice: summaryData.capRemainingBeforeInvoice ?? null,
    areCostsCapped: summaryData.areCostsCapped ?? false,
    areCostsDiscounted: summaryData.areCostsDiscounted ?? false,
});

export const mapInvoiceMatterFormDataToAdjustmentDto = (
    form: InvoiceMatterForm,
    invoiceId: number
): InvoiceMatterAdjustmentDto => ({
    invoiceId,
    matterId: form.matterId,
    discountRate: form.discountRate ? Math.abs(form.discountRate) : null,
    adjustmentAmount: form.adjustmentAmount,
    assignments: form.invoiceAssignments?.map(({ assignmentId, capAdjustment }) => ({
        assignmentId: assignmentId,
        adjustmentAmount: capAdjustment ?? null,
    })),
    adjustmentAmountForFees: form.adjustmentAmountForFees,
    adjustmentAmountForExpensesWithoutVat: form.adjustmentAmountForExpensesWithoutVat,
    adjustmentAmountForExpensesWithVat: form.adjustmentAmountForExpensesWithVat,
    capRemainingBeforeInvoice: form.capRemainingBeforeInvoice,
    areCostsCapped: form.areCostsCapped,
    areCostsDiscounted: form.areCostsDiscounted,
});
